<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card-title class="d-flex align-center">
          <v-btn icon variant="text" @click="$router.back()" class="mr-2">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <div>
            <div class="text-h6">{{ isEdit ? "Edit Task" : "Create Task" }}</div>
            <div class="text-caption text-grey">{{ isEdit && task ? task.title : "Fill in the details below" }}</div>
          </div>
          <v-spacer />
          <audit-history-button
            v-if="isEdit && id"
            entity-type="Task"
            :record-id="id"
            title="Task Change History"
          />
        </v-card-title>
      </v-col>
    </v-row>

    <v-row v-if="loading">
      <v-col cols="12" class="text-center py-8">
        <v-progress-circular indeterminate color="primary" />
        <div class="mt-4">Loading task...</div>
      </v-col>
    </v-row>

    <v-row v-else>
      <v-col cols="12" md="8" offset-md="2">
        <v-card variant="outlined">
          <v-card-text class="pt-4">
            <TaskForm
              v-model="taskData"
              :finding="finding"
              :users="users"
              :isEdit="isEdit"
              :zoneId="zoneId"
              @saved="onTaskSaved"
              @cancel="onCancel"
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import TaskForm from "@/components/TaskForm.vue";
import { getTaskById } from "@/api/tasks";
import { fetchUsers } from "@/api/dropdowns";
import { useToast } from "vue-toastification";

export default {
  name: "Task",
  components: {
    TaskForm,
  },
  props: {
    isEdit: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: null,
    },
    zoneId: {
      type: [String, Number],
      default: null,
    },
    findingId: {
      type: String,
      default: null,
    },
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      loading: false,
      task: null,
      taskData: {},
      finding: null,
      users: [],
    };
  },
  async mounted() {
    await this.loadData();
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        this.users = (await fetchUsers().catch(() => [])) || [];

        // Load task if editing
        if (this.isEdit && this.id) {
          this.task = await getTaskById(this.id);
          this.taskData = {
            id: this.task.taskId,
            title: this.task.title || "",
            description: this.task.description || "",
            zoneId: this.task.zoneId,
            findingId: this.task.findingId,
            assignedTo: this.task.assignedTo,
            assignedToType: this.task.assignedToType ?? 1,
            assignedToContractorId: this.task.assignedToContractorId,
            dueDate: this.task.dueDate,
            estimatedCost: this.task.estimatedCost,
            priority: this.task.priority,
            status: this.task.status,
            latitude: this.task.latitude,
            longitude: this.task.longitude,
            accuracy: this.task.accuracy,
          };

          // Load finding if task has findingId
          if (this.task.findingId) {
            try {
              const { getById } = await import("@/api/findings");
              this.finding = await getById(this.task.findingId);
            } catch (err) {
              // Finding not found or error - continue without it
            }
          }
        } else {
          // Create mode - initialize empty task
          this.taskData = {
            title: "",
            description: "",
            zoneId: this.zoneId || null,
            findingId: this.findingId || null,
            assignedTo: null,
            assignedToType: 1,
            assignedToContractorId: null,
            dueDate: null,
            estimatedCost: null,
            priority: null,
            status: null, // blank on create — user must choose (like Priority)
            latitude: null,
            longitude: null,
            accuracy: null,
          };

          // Load finding if findingId prop provided
          if (this.findingId) {
            try {
              const { getById } = await import("@/api/findings");
              this.finding = await getById(this.findingId);
              // Pre-populate location from finding
              if (this.finding) {
                this.taskData.latitude = this.finding.latitude;
                this.taskData.longitude = this.finding.longitude;
                this.taskData.accuracy = this.finding.accuracy;
                this.taskData.zoneId = this.finding.zoneId || this.taskData.zoneId;
              }
            } catch (err) {
              // Finding not found - continue without it
            }
          }
        }
      } catch (error) {
        this.toast.error(error.response?.data?.message || "Failed to load task data");
      } finally {
        this.loading = false;
      }
    },
    onTaskSaved(savedTask) {
      this.toast.success(this.isEdit ? "Task updated successfully" : "Task created successfully");
      // Navigate back to tasks list
      this.$router.push({ name: "TaskList" });
    },
    onCancel() {
      this.$router.back();
    },
  },
};
</script>

<style scoped>
</style>
